import { useState, useRef, useEffect, lazy, Suspense } from 'react'
import { LogOut, ChevronDown, CloudCheck, Users } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'

// Lazy: only signed-in users who open the panel pay for it.
const HouseholdDialog = lazy(() =>
  import('@/components/household/HouseholdDialog').then((m) => ({ default: m.HouseholdDialog }))
)

/**
 * Header indicator for the signed-in state. Shows the user's email in a
 * pill on desktop / collapses to an icon-only avatar on mobile. Clicking
 * opens a popover with:
 *
 *   - User email + "Cloud sync on" status indicator
 *   - "Sign out" action
 *
 * Lives next to the Help / Backup / Notification controls in the
 * header's right cluster — matches the visual weight of those buttons
 * so the layout doesn't shift between signed-in and signed-out states.
 *
 * **Mobile discoverability:** the avatar always shows the chevron in
 * v0.8.12+ (was desktop-only). Users reported missing the menu entirely
 * because the avatar circle didn't read as interactive on phone.
 *
 * **Why no "View landing page" item:** v0.8.13 moved that to the logo
 * in `Header` (clicking the DebtOS wordmark routes to /landing). The
 * logo-as-home is the more conventional pattern; the dropdown was
 * cluttered. The status indicator now uses a state-ish copy ("Cloud
 * sync on" instead of "Syncing to cloud") so users don't misread a
 * persistent badge as an in-progress operation.
 */
export function SignedInBadge() {
  const { isAuthenticated, email, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  // v0.8.51 — Household panel (invite / members / display name).
  const [householdOpen, setHouseholdOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click — same convention as NotificationBell.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!isAuthenticated || !email) return null

  const initial = email[0]?.toUpperCase() ?? '?'

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1.5 hover:bg-slate-800 rounded-lg transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${email}`}
        title="Account menu (sign out, view landing)"
      >
        <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center text-xs font-semibold">
          {initial}
        </span>
        <span className="hidden md:inline text-sm text-slate-300 max-w-[160px] truncate">
          {email}
        </span>
        {/* Chevron is now visible on every viewport so the avatar reads
            as a menu trigger, not a static badge. Mobile users were
            missing the dropdown entirely without this affordance. */}
        <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-slate-800">
            <div className="flex items-center gap-2 text-xs text-emerald-300">
              <CloudCheck className="w-3.5 h-3.5" />
              {/* "Cloud sync on" reads as a persistent state. The previous
                  "Syncing to cloud" copy was misread as an in-progress
                  operation — users thought the app was waiting on a
                  background sync to finish. */}
              <span>Cloud sync on</span>
            </div>
            <div className="text-sm text-white truncate mt-0.5">{email}</div>
          </div>
          <button
            onClick={() => {
              setOpen(false)
              setHouseholdOpen(true)
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            role="menuitem"
          >
            <Users className="w-4 h-4" />
            Household
          </button>
          <button
            onClick={async () => {
              setOpen(false)
              await signOut()
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            role="menuitem"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      ) : null}
      {householdOpen ? (
        <Suspense fallback={null}>
          <HouseholdDialog open onClose={() => setHouseholdOpen(false)} />
        </Suspense>
      ) : null}
    </div>
  )
}
